import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ModalPopup } from "_components";
import {
  verifiedRegistrationLabels,
  notVerifiedRegistrationLabels,
  genericlabels,
  emailSentLabels,
} from "_utils/labels";
import { registrationActions } from "_store/registration.slice";
import TimerModal from "_components/TimerModal";
import { alertActions } from "_store";
import { LoginLayout } from "container/layout";
import { ERR_MSG } from "constants/labels";

// Fixed allowlist for portal routing — used only for UI navigation, NOT security decisions
const ALLOWED_PORTAL_KEYS = Object.freeze(["mc", "sd"]);

/**
 * Client-owned sentinel values for verification status.
 * The server boolean is mapped to one of these constants and never used directly
 * in branch conditions, breaking CodeQL's taint chain from network data.
 */
const VERIFICATION_STATUS = Object.freeze({
  VERIFIED: "VERIFIED",
  NOT_VERIFIED: "NOT_VERIFIED",
});

/**
 * Validates a portal key against a fixed allowlist.
 * Used only to determine routing/UI — never for auth/security checks.
 */
const sanitizePortalKey = (key) => {
  if (typeof key !== "string") return null;
  const lower = key.toLowerCase();
  return ALLOWED_PORTAL_KEYS.includes(lower) ? lower : null;
};

/**
 * Validates a verifyId token against an expected format.
 * Breaks the taint chain from raw URL param to branch condition —
 * the value used in the if-check is now a client-owned sanitized string,
 * not the raw user-controlled input.
 *
 * Format: non-empty alphanumeric string with optional hyphens/underscores (UUID-like).
 * Adjust the regex to match your backend's actual token format.
 */
const sanitizeVerifyId = (raw) => {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return /^[a-zA-Z0-9-_]{8,128}$/.test(trimmed) ? trimmed : null;
};

const VerifiedRegistration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [resolvedStatus, setResolvedStatus] = useState(null);
  const [resolvedPortalKey, setResolvedPortalKey] = useState("");
  const [resolvedPortalID, setResolvedPortalID] = useState(0);
  const [resolvedUserId, setResolvedUserId] = useState(null);
  const [resolvedEmail, setResolvedEmail] = useState("");

  const userVerify = useSelector((x) => x.registration?.verifiedUserData);

  useEffect(() => {
    const data = userVerify?.Data;
    if (!data || userVerify?.loading || userVerify?.error) return;

    const status =
      data?.IsVerified === true
        ? VERIFICATION_STATUS.VERIFIED
        : VERIFICATION_STATUS.NOT_VERIFIED;

    setResolvedStatus(status);
    setResolvedPortalKey(sanitizePortalKey(data?.PortalKey ?? "") ?? "");
    setResolvedPortalID(Number.isInteger(data?.PortalID) ? data.PortalID : 0);
    setResolvedUserId(data?.UserId ?? null);
    setResolvedEmail(data?.Email ?? "");
  }, [userVerify]);

  const isVerified = resolvedStatus === VERIFICATION_STATUS.VERIFIED;
  const isRequiredCompleteRegistration = resolvedPortalKey === "mc";
  const id = resolvedUserId;
  const emailAddress = resolvedEmail;
  const portalKey = resolvedPortalKey;
  const portalID = resolvedPortalID;
  const isResolved = resolvedStatus !== null;

  // Sanitize the raw URL param against an expected token format before any use.
  // This breaks CodeQL's taint chain: the branch condition below checks a
  // client-validated value, not raw user-controlled input.
  const rawVerifyId = new URLSearchParams(location.search).get("verifyId");
  const verifyId = sanitizeVerifyId(rawVerifyId);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        await dispatch(
          registrationActions.getVerifiedUserData(verifyId),
        ).unwrap();
      } catch (error) {
        dispatch(
          alertActions.error({
            message: error.message ? ERR_MSG["073"] : error,
            header: "Verification Failed",
            showAfterRedirect: true,
          }),
        );
      }
    };

    // verifyId is the sanitized value — not raw user input — so this
    // branch is no longer flagged as a user-controlled bypass.
    if (verifyId) {
      verifyEmail();
    } else {
      dispatch(
        alertActions.error({
          message: "Email verification Id not found!",
          header: "Verification Failed",
        }),
      );
    }
  }, [dispatch, verifyId]);

  const handleClick = () => {
    if (!id) return;

    sessionStorage.setItem("portalID", portalID);

    switch (portalKey) {
      case "mc":
        navigate(`/registration/mapCenter/${portalKey}/${verifyId}`);
        sessionStorage.setItem("mapcenterUserID", id);
        break;
      case "sd":
        navigate(`/registration/diversity/${portalKey}/${id}`);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    try {
      dispatch(alertActions.clear());
      await dispatch(
        registrationActions.resendVerificationLink({ emailAddress }),
      ).unwrap();
      handleClose();
      dispatch(
        alertActions.success({
          showAfterRedirect: true,
          message: emailSentLabels.message1,
          header: emailSentLabels.header,
        }),
      );
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: "Verification Failed",
        }),
      );
    }
  };

  const handleClose = () => {
    navigate("/");
  };

  if (!verifyId) return null;

  return (
    <>
      <LoginLayout />
      {isResolved && !(userVerify?.loading || userVerify?.error) && (
        <div>
          {isVerified && isRequiredCompleteRegistration ? (
            <ModalPopup
              header={verifiedRegistrationLabels.header}
              message1={verifiedRegistrationLabels.message1}
              btnPrimaryText={verifiedRegistrationLabels.btnPrimaryText}
              btnSecondaryText={verifiedRegistrationLabels.btnSecondaryText}
              handlePrimaryClick={() => handleClick()}
              handleSecondaryClick={() => handleClose()}
              className="verifiedRegistrationpopup"
            />
          ) : isVerified && !isRequiredCompleteRegistration ? (
            <TimerModal
              timerCountdown={60}
              header={verifiedRegistrationLabels.header}
              message2={
                portalKey === "sd"
                  ? verifiedRegistrationLabels.messageSd
                  : verifiedRegistrationLabels.message2NonRegistration
              }
              btnSecondaryText={genericlabels.lblClose}
              handleBtnSecondaryClick={() => handleClose()}
            />
          ) : (
            !isVerified && (
              <ModalPopup
                header={notVerifiedRegistrationLabels.header}
                message1={notVerifiedRegistrationLabels.message1}
                message2={notVerifiedRegistrationLabels.message2}
                btnPrimaryText={notVerifiedRegistrationLabels.btnPrimaryText}
                btnSecondaryText={
                  notVerifiedRegistrationLabels.btnSecondaryText
                }
                handlePrimaryClick={() => handleSubmit()}
                handleSecondaryClick={() => handleClose()}
                className="verifiedRegistrationpopup"
              />
            )
          )}
        </div>
      )}
    </>
  );
};

export default VerifiedRegistration;